<template>
  <div>
    <!-- Inline Mode -->
    <div v-show="mode === 'inline'">
      <occurrences-form
        ref="occurrencesFormRef"
        :owner="owner"
        :owner-id="ownerId"
        :save-label="saveLabel"
        :elem-id="formElemId"
        :show-save-btn="showInlineSaveBtn"
        :model-value="modelValue"
        @update:model-value="handleModelValueUpdate"
        @input="handleInput">
      </occurrences-form>
    </div>

    <!-- Modal Mode - Button to open centralized OccurrencesModal via eventBus -->
    <div v-show="mode === 'modal'" class="add-button-container">
      <Button
        data-testid="ocorrencia-add-btn"
        :label="addLabel"
        icon="pi pi-plus"
        severity="primary"
        size="small"
        @click="openOccurrencesModal"
      />
    </div>

    <!-- Table -->
    <div v-show="mode === 'modal'">
      <occurrences-table
        ref="occurrencesTableRef"
        :owner="owner"
        :owner-id="ownerId"
        :allow-delete="allowDelete"
        :datatable-language-url="datatableLanguageUrl"
        @row-click="handleRowClick"
        @delete-occurrence="handleDeleteOccurrence">
      </occurrences-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import OccurrencesForm from './OccurrencesForm.vue';
import OccurrencesTable from './OccurrencesTable.vue';

type OwnerId = string | number | null | undefined;
type AllowDelete = string | boolean;
type Mode = 'modal' | 'inline';

interface OccurrencesTableInstance {
  loadData: () => void;
  clearData: () => void;
}

interface OccurrencesFormInstance {
  salvaNovaOcorrencia: () => Promise<void>;
  resetForm: () => void;
}

const props = withDefaults(defineProps<{
  owner?: string;
  ownerId?: OwnerId;
  addLabel?: string;
  mode?: Mode;
  showInlineSaveBtn?: boolean;
  modelValue?: string;
  value?: string;
  allowDelete?: AllowDelete;
  modalTitle?: string;
  descriptionLabel?: string;
  closeLabel?: string;
  saveLabel?: string;
  formElemId?: string;
  datatableLanguageUrl?: string;
}>(), {
  owner: '',
  ownerId: null,
  addLabel: 'Adicionar',
  mode: 'modal',
  showInlineSaveBtn: false,
  modelValue: '',
  value: '',
  allowDelete: true,
  modalTitle: 'Nova Ocorrência',
  descriptionLabel: 'Descrição',
  closeLabel: 'Fechar',
  saveLabel: 'Salvar',
  formElemId: 'inputNovaOcorrencia',
  datatableLanguageUrl: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
  'save-success': [];
  'row-click': [rowData: unknown];
  'delete-occurrence': [id: string | number];
}>();

const occurrencesFormRef = ref<OccurrencesFormInstance | null>(null);
const occurrencesTableRef = ref<OccurrencesTableInstance | null>(null);

const handleSaveSuccess = (): void => {
  if (occurrencesTableRef.value) {
    occurrencesTableRef.value.loadData();
  }
  emit('save-success');
  log('OccurrencesPage', 'handleSaveSuccess', 'Save successful, refreshing table');
};

const handleModelValueUpdate = (value: string): void => {
  emit('update:modelValue', value);
};

const handleInput = (value: string): void => {
  emit('input', value);
};

const handleRowClick = (rowData: unknown): void => {
  emit('row-click', rowData);
  log('OccurrencesPage', 'handleRowClick', 'Row clicked', rowData);
};

const handleDeleteOccurrence = (id: string | number): void => {
  emit('delete-occurrence', id);
  log('OccurrencesPage', 'handleDeleteOccurrence', 'Occurrence deleted', id);
};

const init = (owner: string | undefined, ownerID: OwnerId): void => {
  if (owner && ownerID) {
    log('OccurrencesPage', 'init', 'Initializing with owner and ownerID', { owner, ownerID });
  }
};

const loadData = (): void => {
  if (occurrencesTableRef.value) {
    occurrencesTableRef.value.loadData();
  }
};

const clearData = (): void => {
  if (occurrencesTableRef.value) {
    occurrencesTableRef.value.clearData();
  }
};

const openOccurrencesModal = (): void => {
  log('OccurrencesPage', 'openOccurrencesModal', 'Opening modal via eventBus', { owner: props.owner, ownerId: props.ownerId });
  eventBus.emit('modal.occurrences.open', {
    owner: props.owner,
    ownerId: props.ownerId,
    elemId: props.formElemId
  });
};

const openModal = (): void => {
  openOccurrencesModal();
};

const closeModal = (): void => {
  eventBus.emit('modal.occurrences.close');
};

watch(() => props.ownerId, (newVal) => {
  if (newVal && newVal !== "0" && newVal !== 0) {
    init(props.owner, newVal);
  }
}, { immediate: true });

const handleOccurrencesSaved = (): void => {
  log('OccurrencesPage', 'handleOccurrencesSaved', 'Occurrence saved, refreshing table');
  handleSaveSuccess();
};

onMounted(() => {
  eventBus.on('occurrences.saved', handleOccurrencesSaved);

  if (props.ownerId && props.ownerId !== "0" && props.ownerId !== 0) {
    nextTick(() => {
      init(props.owner, props.ownerId);
    });
  }
});

onBeforeUnmount(() => {
  eventBus.off('occurrences.saved', handleOccurrencesSaved);
  clearData();
});

defineExpose({
  init,
  loadData,
  clearData,
  openModal,
  closeModal
});
</script>

<style scoped>
/* Page-specific styles */
.add-button-container {
  margin-bottom: 20px;
}
</style>
